import type { ReactNode } from 'react';
import type { Lens } from '@hookform/lenses';
import { useWatch } from 'react-hook-form';
import { match, P } from 'ts-pattern';
import { z } from 'zod/v4';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxcRegistrationV1ProfileType } from '@interxfi/types/generated/api';
import { useProgramRequiresSuitabilityQuestions } from '../../shared-utils/registration-permission-helpers';
import {
  defaultIndividualProfileFormValues,
  formValuesToIndividualProfile,
  getInitialFormValuesForIndividualProfile,
  IndividualProfileForm,
  type IndividualProfileFormValues,
  individualProfileFormSchema,
} from './individual/individual-profile-form';
import {
  formValuesToOrganizationProfile,
  getInitialFormValuesForOrganizationProfile,
  OrganizationProfileForm,
  type OrganizationProfileFormValues,
  onSubmitOrganizationProfileFormSchema,
  organizationProfileFormSchema,
} from './organization/organization-profile-form';

export const registrationProfileFormSchema = z.discriminatedUnion('type', [individualProfileFormSchema, organizationProfileFormSchema]);

export const registrationProfileOnSubmitFormSchema = z.discriminatedUnion('type', [
  individualProfileFormSchema,
  onSubmitOrganizationProfileFormSchema,
]);

export type RegistrationProfileFormValues = z.infer<typeof registrationProfileFormSchema>;

export function getInitialFormValuesForRegistrationProfile(profile: IxcRegistrationV1ProfileType | undefined) {
  return match(profile)
    .with({ individual: P.not(P.nullish) }, (p) => getInitialFormValuesForIndividualProfile(p.individual))
    .with({ organization: P.not(P.nullish) }, (p) => getInitialFormValuesForOrganizationProfile(p.organization))
    .otherwise(() => defaultIndividualProfileFormValues);
}

export function formValuesToRegistrationProfileType(values: RegistrationProfileFormValues): IxcRegistrationV1ProfileType {
  return match(values)
    .with({ type: 'individual' }, (profile) => ({ individual: formValuesToIndividualProfile(profile) }))
    .with({ type: 'organization' }, (profile) => ({ organization: formValuesToOrganizationProfile(profile) }))
    .exhaustive();
}

interface RegistrationProfileFormProps {
  lens: Lens<RegistrationProfileFormValues>;
  populateSampleDataBannerElement?: ReactNode;
  programId: string | undefined;
}

export function RegistrationProfileForm({ lens, populateSampleDataBannerElement, programId }: RegistrationProfileFormProps) {
  const selectedProfileType = useWatch(lens.focus('type').interop());
  const requiresSuitabilityQuestions = useProgramRequiresSuitabilityQuestions(programId);

  switch (selectedProfileType) {
    case 'individual':
      return (
        <VerticalStack gap={7}>
          <Typography.Level1 as="h1">Individual Info</Typography.Level1>

          {populateSampleDataBannerElement}

          <IndividualProfileForm lens={lens.narrow<IndividualProfileFormValues>()} requiresSuitabilityQuestions={requiresSuitabilityQuestions} />
        </VerticalStack>
      );
    case 'organization':
      return (
        <VerticalStack gap={7}>
          <Typography.Level1 as="h1">Organization Info</Typography.Level1>

          {populateSampleDataBannerElement}

          <OrganizationProfileForm lens={lens.narrow<OrganizationProfileFormValues>()} programId={programId} />
        </VerticalStack>
      );
    default:
      return null;
  }
}
